import { useOmniSession } from '@omni/auth'
import { usePathname } from './lib/navigation'
import { AuthCallback } from './components/AuthCallback'
import { Chrome } from './components/Chrome'
import { LoginScreen } from './components/LoginScreen'
import { Splash } from './components/Splash'
import { PageViewRecorder } from './lib/page-views'
import { FrontDoorGate, LAUNCHER, LauncherChrome, rememberNextForSignIn, signedOutRedirect } from '@shell-front-door'
import { activeInstance } from './instances'
import * as surfaceAppsModule from '@shell-surface-apps'

// The one-click "Turn off these emails" page an email copy of a notification
// links to (instance.notifications): no sign-in, so it renders before the
// sign-in gate. Builds without it never take this path.
const EmailOffPage =
  activeInstance.notifications === true
    ? ((surfaceAppsModule as Record<string, unknown>).EmailOffPage as (() => JSX.Element) | undefined)
    : undefined

export default function App() {
  const pathname = usePathname()
  const session = useOmniSession()

  // /auth/callback?app=… must render regardless of auth state: it is where
  // the magic link lands and the session gets picked up.
  if (pathname.startsWith('/auth/callback')) return <AuthCallback />
  if (EmailOffPage && pathname === '/email-off') return <EmailOffPage />
  // Build-time constant: the mortgage-man build folds this to its branded
  // label, every other build keeps the stock string byte-identical.
  if (session.loading)
    return (
      <Splash
        label={
          import.meta.env.VITE_INSTANCE_SLUG === 'nateva'
            ? 'Nateva Intelligence is starting…'
            : import.meta.env.VITE_INSTANCE_SLUG === 'mortgage-man' ||
          import.meta.env.VITE_INSTANCE_SLUG === 'mba-brands' ||
          import.meta.env.VITE_INSTANCE_SLUG === 'asdm-solution' ||
          import.meta.env.VITE_INSTANCE_SLUG === 'elevate-wealth'
            ? 'Your Command Centre is starting…'
            : import.meta.env.VITE_INSTANCE_SLUG === 'ona-insurance-brokers'
              ? 'Your Command Center is starting…'
              : 'Loading Omni Command…'
        }
      />
    )
  // Signed in at all = in. In single-project mode (one tenant DB, the normal
  // posture) a successful sign-in fills ALL THREE app slots at once, which
  // makes isPartiallyAuthed FALSE (count===3), and gating on it alone bounced
  // fully-authed users straight back to the login screen (2026-08-29).
  if (!session.isFullyAuthed && !session.isPartiallyAuthed) {
    // Customer front door (EDNA-CC docs/customer-front-door.md, section 5).
    // __FRONT_DOOR__ is a build-time constant (vite.config.ts): false for
    // every instance without frontDoor.domain, so this block is dropped and
    // they show the sign-in screen exactly as before.
    if (__FRONT_DOOR__) {
      // An app build on the customer's domain goes to the front door to sign
      // in (back here after); localhost and drafts sign in right here.
      const to = signedOutRedirect()
      if (to) {
        window.location.replace(to)
        return <Splash label="Taking you to sign in…" />
      }
      rememberNextForSignIn()
    }
    return <LoginScreen />
  }
  // A front door with the launcher layout (instance frontDoor.layout) gets a
  // slim top bar instead of the Command Centre side menu, on every page.
  return (
    <>
      <PageViewRecorder />
      {__FRONT_DOOR__ ? (
        <FrontDoorGate>{LAUNCHER ? <LauncherChrome /> : <Chrome />}</FrontDoorGate>
      ) : (
        <Chrome />
      )}
    </>
  )
}
